/* @jsx h */
import { defineComponent, h, ref, computed, watch } from "vue";
import { CreateCustomerAddressData } from "@lemmonhq/types";
import { SegmentSelect } from "@lemmonhq/uikit";
import { CustomerAddressManualForm } from "./CustomerAddressManualForm";
import { CustomerAddressSearch, SearchResult } from "./CustomerAddressSearch";
import { CustomerAddressContactForm } from "./CustomerAddressContactForm";

export interface CustomerAddressCreateProps {
  form: CreateCustomerAddressData;
  onUpdate: (field: keyof CreateCustomerAddressData, value: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
  isSubmitting: boolean;
  errorMessage: string;
}

export const CustomerAddressCreate =
  defineComponent<CustomerAddressCreateProps>({
    name: "CustomerAddressCreate",
    props: [
      "form",
      "onUpdate",
      "onSubmit",
      "onCancel",
      "isSubmitting",
      "errorMessage",
    ],
    setup(props) {
      type Mode = "manual" | "search" | "contact";
      const mode = ref<Mode>("search");
      const lastSelection = ref<SearchResult | null>(null);

      const availableModes = computed(() => {
        const modes: Array<{ value: Mode; label: string }> = [
          { value: "search", label: "Search maps" },
          { value: "manual", label: "Enter manually" },
        ];
        if (lastSelection.value) {
          modes.push({
            value: "contact",
            label: "Contact details",
          });
        }
        return modes;
      });

      const activeModeValue = computed<Mode>(() => {
        const values = availableModes.value.map((option) => option.value);
        return values.includes(mode.value) ? mode.value : "manual";
      });

      const handleModeChange = (value?: Mode) => {
        const next = value || "manual";
        if (next === "contact" && !lastSelection.value) {
          mode.value = "manual";
          return;
        }
        mode.value = next;
      };

      const applySearchResult = (result: SearchResult) => {
        const updates: Array<[keyof CreateCustomerAddressData, string]> = [
          ["street", result.street],
          ["postal_code", result.postal_code],
          ["city", result.city],
        ];

        if (result.email) {
          updates.push(["email", result.email]);
        }
        if (result.phone) {
          updates.push(["phone", result.phone]);
        }
        if (Number.isFinite(result.latitude)) {
          updates.push(["latitude", String(result.latitude)]);
        }
        if (Number.isFinite(result.longitude)) {
          updates.push(["longitude", String(result.longitude)]);
        }

        updates.forEach(([field, value]) => {
          props.onUpdate(field, value);
        });

        lastSelection.value = result;
        mode.value = "contact";
      };

      const resetSelection = () => {
        lastSelection.value = null;
        if (mode.value === "contact") {
          mode.value = "manual";
        }
      };

      watch(
        () => lastSelection.value,
        (value) => {
          if (!value && mode.value === "contact") {
            mode.value = "manual";
          }
        },
      );

      return () => (
        <div class="space-y-4">
          <div class="flex flex-1 items-center justify-between gap-3">
            <div class="flex-1">
              <div class="flex gap-2">
                <h4 class="text-lg font-semibold text-accent/80">
                  New delivery address
                </h4>
                <button
                  class="btn btn-sm btn-ghost"
                  onClick={() => {
                    resetSelection();
                    props.onCancel();
                  }}
                  disabled={props.isSubmitting}
                >
                  Close
                </button>
              </div>
              <p class="text-sm text-base-content/60">
                Search the map or enter details manually, then confirm contacts.
              </p>
            </div>
            <div>
              <SegmentSelect
                className="w-full"
                options={availableModes.value}
                value={activeModeValue.value}
                onChange={(value) =>
                  handleModeChange(value as Mode | undefined)
                }
              />
            </div>
          </div>

          {activeModeValue.value === "search" ? (
            <CustomerAddressSearch
              onApplyResult={applySearchResult}
              onCancel={() => {
                handleModeChange(lastSelection.value ? "contact" : "manual");
              }}
            />
          ) : activeModeValue.value === "manual" ? (
            <CustomerAddressManualForm
              form={props.form}
              onUpdate={props.onUpdate}
              errorMessage={props.errorMessage}
              isSubmitting={props.isSubmitting}
              onSubmit={props.onSubmit}
              onCancel={() => {
                resetSelection();
                props.onCancel();
              }}
              onClearCoordinates={() => {
                props.onUpdate("latitude", "");
                props.onUpdate("longitude", "");
              }}
            />
          ) : (
            <CustomerAddressContactForm
              form={props.form}
              onUpdate={props.onUpdate}
              onSubmit={props.onSubmit}
              onCancel={() => {
                props.onCancel();
                resetSelection();
              }}
              onEditDetails={() => handleModeChange("manual")}
              isSubmitting={props.isSubmitting}
              errorMessage={props.errorMessage}
              selectionSummary={
                lastSelection.value
                  ? {
                      label: lastSelection.value.label,
                      street: lastSelection.value.street,
                      postal_code: lastSelection.value.postal_code,
                      city: lastSelection.value.city,
                    }
                  : null
              }
            />
          )}
        </div>
      );
    },
  });
